import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/colors';

const FeatureContainer = styled.section`
  padding: 80px 0;
  background: white;
`;

const FeatureContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin: 0;
    padding: 0 20px;
  }
  
  @media (max-width: 480px) {
    padding: 0 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 48px;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin-bottom: 16px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: ${colors.textSecondary};
  margin-bottom: 60px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

// 데스크탑용 단계별 레이아웃
const DesktopLayout = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const StepsNav = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 60px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 10%;
    right: 10%;
    height: 2px;
    background: ${colors.borderPrimary};
    z-index: 1;
  }
`;

const StepButton = styled.button<{ active: boolean; stepNumber: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 20px;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
  }
`;

const StepCircle = styled.div<{ active: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${p => p.active ? colors.primary : 'white'};
  border: 3px solid ${p => p.active ? colors.primary : colors.borderPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: ${p => p.active ? 'white' : colors.textSecondary};
  margin-bottom: 12px;
  transition: all 0.3s ease;
  box-shadow: ${p => p.active ? '0 8px 24px rgba(36, 128, 255, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)'};
`;

const StepLabel = styled.div<{ active: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.active ? colors.primary : colors.textSecondary};
  white-space: nowrap;
`;

const StepContent = styled.div`
  background: ${colors.backgroundSecondary};
  border-radius: 24px;
  padding: 60px 80px;
  min-height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    text-align: left;
    display: block;
  }
`;

const ContentText = styled.div`
  max-width: 800px;
  width: 100%;
  text-align: left;
  background: transparent;
  border-radius: 16px;
  padding: 40px 10px 40px 80px;
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 20px;
    margin: 0;
  }
`;

const ContentTitle = styled.h3`
  font-size: 32px;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin-bottom: 20px;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 16px;
    text-align: left;
  }
`;

const ContentDescription = styled.p`
  font-size: 18px;
  color: ${colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 24px;
  text-align: left;
  
  @media (max-width: 730px) {
    display: none;
  }
`;

const ContentBenefits = styled.ul`
  list-style: none;
  padding: 0;
  text-align: left;
  
  @media (max-width: 768px) {
    text-align: left;
  }
  
  li {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    margin-bottom: 12px;
    font-size: 16px;
    color: ${colors.textSecondary};
    
    @media (max-width: 768px) {
      justify-content: flex-start;
      gap: 8px;
      margin-bottom: 10px;
      font-size: 14px;
      line-height: 1.4;
      text-align: left;
    }
    
    &::before {
      content: '✨';
      color: ${colors.success};
      font-weight: bold;
      font-size: 18px;
      flex-shrink: 0;
      
      @media (max-width: 768px) {
        font-size: 16px;
      }
    }
  }
`;

// 모바일용 타임라인 레이아웃
const MobileLayout = styled.div`
  @media (min-width: 769px) {
    display: none;
  }
`;

const Timeline = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 30px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${colors.borderPrimary};
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-left: 80px;
  margin-bottom: 60px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineCircle = styled.div`
  position: absolute;
  left: 6px;
  top: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  color: white;
  box-shadow: 0 4px 12px rgba(36, 128, 255, 0.3);
`;

const TimelineContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid ${colors.borderLight};
`;

const TimelineTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin-bottom: 12px;
  
  @media (max-width: 350px) {
    font-size: 18px;
    margin-bottom: 10px;
  }
`;

const TimelineDescription = styled.p`
  font-size: 14px;
  color: ${colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 16px;
  
  @media (max-width: 730px) {
    display: none;
  }
  
  @media (max-width: 350px) {
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 14px;
  }
`;

const TimelineBenefits = styled.ul`
  list-style: none;
  padding: 0;
  
  li {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 13px;
    color: ${colors.textSecondary};
    
    @media (max-width: 350px) {
      gap: 6px;
      margin-bottom: 6px;
      font-size: 12px;
      line-height: 1.4;
    }
    
    &::before {
      content: '✨';
      color: ${colors.success};
      font-weight: bold;
      font-size: 12px;
      flex-shrink: 0;
    }
  }
`;

interface Step {
  number: number;
  label: string;
  title: string;
  description: string;
  benefits: string[];
}

const steps: Step[] = [
  {
    number: 1,
    label: '투자 고민 등록',
    title: '종목별 투자 고민을 체계적으로 기록',
    description: '매수/매도 결정에 대한 고민을 감정과 함께 기록하고, 시장 상황과 기업 분석 정보를 수집합니다.',
    benefits: [
      '종목별 고민 노트 생성 및 관리',
      '감정 상태와 투자 결정 연결',
      '시장 동향 및 기업 정보 수집',
      '투자 목표 및 전략 설정'
    ]
  },
  {
    number: 2,
    label: 'AI 멘토 분석',
    title: '9명의 투자 전설 관점에서 종합 분석',
    description: 'AI가 사용자의 고민과 시장 데이터를 9명의 투자 멘토 철학에 따라 분석하여 투자 조언을 제공합니다.',
    benefits: [
      '워렌 버핏 등 9명 멘토 관점',
      '감정, 기술, 뉴스 종합 분석',
      '리스크 및 기회 요소 분석',
      '개인화된 투자 철학 제안'
    ]
  },
  {
    number: 3,
    label: '거래 실행 기록',
    title: '실제 매수-매도 행동을 상세히 기록',
    description: '투자 결정에 따른 실제 거래를 종목, 가격, 수량, 일시별로 기록하고 포트폴리오 변화를 추적합니다.',
    benefits: [
      '매수/매도 거래 상세 기록',
      '평균단가 및 수익률 자동 계산',
      '포트폴리오 실시간 추적',
      '거래 이력 체계적 관리'
    ]
  },
  {
    number: 4,
    label: '성과 분석 및 개선',
    title: 'AI가 투자 성과를 분석하여 맞춤형 피드백 제공',
    description: '거래 후 AI가 투자 성과와 의사결정 과정을 분석하여 개선점을 제시하고 투자 습관을 강화합니다.',
    benefits: [
      '투자 성과 종합 분석 리포트',
      '의사결정 과정 데이터화',
      '투자 습관 개선 제안',
      '지속적인 학습과 발전 가이드'
    ]
  }
];

export const FeatureSection: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <FeatureContainer id="features" data-section="feature">
      <FeatureContent>
        <SectionTitle>
          투자 철학 형성과 습관 개선 여정
        </SectionTitle>
        <SectionSubtitle>
          4단계 과정을 통해 체계적으로 투자 실력을 향상시켜 나가세요
        </SectionSubtitle>

        {/* 데스크탑용 단계별 레이아웃 */}
        <DesktopLayout>
          <StepsNav>
            {steps.map((step, index) => (
              <StepButton
                key={step.number}
                active={activeStep === index}
                stepNumber={step.number}
                onClick={() => setActiveStep(index)}
              >
                <StepCircle active={activeStep === index}>
                  {step.number}
                </StepCircle>
                <StepLabel active={activeStep === index}>
                  {step.label}
                </StepLabel>
              </StepButton>
            ))}
          </StepsNav>

          <StepContent>
            <ContentText>
              <ContentTitle>{steps[activeStep].title}</ContentTitle>
              <ContentDescription>{steps[activeStep].description}</ContentDescription>
              <ContentBenefits>
                {steps[activeStep].benefits.map((benefit, index) => (
                  <li key={index}>{benefit}</li>
                ))}
              </ContentBenefits>
            </ContentText>
          </StepContent>
        </DesktopLayout>

        {/* 모바일용 타임라인 레이아웃 */}
        <MobileLayout>
          <Timeline>
            {steps.map((step, index) => (
              <TimelineItem key={step.number}>
                <TimelineCircle>{step.number}</TimelineCircle>
                <TimelineContent>
                  <TimelineTitle>
                    {step.label}
                  </TimelineTitle>
                  <TimelineDescription>{step.description}</TimelineDescription>
                  <TimelineBenefits>
                    {step.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex}>{benefit}</li>
                    ))}
                  </TimelineBenefits>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </MobileLayout>
      </FeatureContent>
    </FeatureContainer>
  );
}; 